// The one place that stores and reads the cookie choice. The banner writes here,
// public/pixel-bootstrap.js reads the same keys before loading the Meta/TikTok pixels,
// and every tracking call checks hasAnalyticsConsentNow() first.
export type CookiePreferences = { necessary: boolean; functional: boolean; analytics: boolean };

export const COOKIE_CONSENT_KEY = "chillwerk_cookie_consent";
export const COOKIE_PREFERENCES_KEY = "chillwerk_cookie_preferences";
/** Old key a previous version read; kept only as a read fallback. */
const LEGACY_KEY = "cookie-consent";
export const CONSENT_EVENT = "cookie-consent-changed";

type StorageLike = Pick<Storage, "getItem" | "setItem">;
const store = (): StorageLike | null => {
  try { return typeof window !== "undefined" ? window.localStorage : null; } catch { return null; }
};

/** The saved choice, or null when the visitor has not chosen yet. */
export function readCookiePreferences(s: StorageLike | null = store()): CookiePreferences | null {
  if (!s) return null;
  try {
    const consent = s.getItem(COOKIE_CONSENT_KEY);
    const prefs = s.getItem(COOKIE_PREFERENCES_KEY);
    if (consent && prefs) {
      const p = JSON.parse(prefs);
      return { necessary: true, functional: !!p.functional, analytics: !!p.analytics };
    }
    const legacy = s.getItem(LEGACY_KEY);
    if (legacy) {
      const p = JSON.parse(legacy);
      return { necessary: true, functional: !!p.functional, analytics: !!p.analytics };
    }
  } catch { /* corrupt or blocked storage = no choice */ }
  return null;
}

/** True only when the visitor explicitly allowed analytics/marketing cookies. */
export function hasAnalyticsConsentNow(s: StorageLike | null = store()): boolean {
  return readCookiePreferences(s)?.analytics === true;
}

export function saveCookiePreferences(prefs: CookiePreferences, s: StorageLike | null = store()) {
  const clean = { necessary: true, functional: !!prefs.functional, analytics: !!prefs.analytics };
  try {
    s?.setItem(COOKIE_CONSENT_KEY, "true");
    s?.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(clean));
  } catch { /* storage blocked */ }
  if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: clean }));
  return clean;
}
